// The board: the prompt-section console's shape, over every kind.
//
// The register that replaced that page is a TABLE - fourteen sortable columns,
// a row per experiment - and a table is the right tool for "which of these
// ninety-seven is worth my attention". It is the wrong one for the job an
// operator actually sits down to do, which is: read what this thing says, then
// move its dial. That job wants a card you can read, the control on the card,
// and the text beside it.
//
// So this is the sections page's own layout, generalised: tabs across the top
// (by KIND rather than by agent, because a kind is what changes the shape of
// the control), a sentence saying what the tab holds, a list of cards each
// carrying its own allocation control, and the inspector as a sticky panel to
// the right. The table is still there behind a toggle for the times the
// question really is "which one".
//
// One thing the old page did NOT have and this does: a search box. It listed
// 133 sections for one agent with no way to find one by name, which was
// tolerable at 133 and is not at ninety-seven experiments across six kinds.
import { useMemo, useState } from "react";

import { Badge, Button } from "@recruiter/shared";
import type { AdminExperimentRecord } from "@recruiter/shared";

import { AllocationEditor } from "./experiment-allocation";
import { formatAllocation, isOffDefault, shippedArm } from "./restore-defaults";
import { ExperimentInspector } from "./experiment-inspector";
import {
  KIND_GROUP_ORDER,
  allocatableKind,
  groupLabel,
  registerHaystack,
  stateOf,
  summariseAllocation,
  totalExposuresOf,
} from "../lib/experimentation";
import "./experiment-board.css";

/** How a state reads as a badge. `split` is the one worth looking at, so it is
 *  the only one that gets the accent; `off` and `shipped` are resting states and
 *  a page that shouted about them would be shouting about almost everything. */
function stateTone(state: string): "success" | "warning" | "neutral" {
  if (state === "split") return "success";
  if (state === "killed" || state === "unknown") return "warning";
  return "neutral";
}

function Card({
  row,
  canAct,
  open,
  onOpen,
}: {
  row: AdminExperimentRecord;
  canAct: boolean;
  open: boolean;
  onOpen: () => void;
}) {
  const state = stateOf(row);
  const exposures = totalExposuresOf(row);
  const dialable = allocatableKind(row.kind) !== null;
  const offDefault = isOffDefault(row.allocation, row.defaultAllocation);
  // "off" means the EXPERIMENT is not splitting anybody. For a declaration that ships a
  // non-baseline arm that is true and misleading at once, so the card says what the code
  // ships beside it. Only shown while the experiment is off: once it is running, the
  // state and the allocation already say what is happening.
  const ships = state === "off" ? shippedArm(row.defaultAllocation) : null;

  return (
    <div className={open ? "exb-card is-open" : "exb-card"}>
      <div className="exb-card__meta">
        <div className="exb-card__name">
          {/* The NAME opens the panel, not the whole card: the card holds a
              slider, and a card-wide click target would open the panel every
              time somebody reached for the dial. */}
          <button
            type="button"
            className="exb-card__open"
            onClick={onOpen}
            aria-expanded={open}
          >
            {row.label || row.key}
          </button>
          <Badge tone={stateTone(state)}>{state}</Badge>
          <Badge tone="neutral">per {row.unit}</Badge>
          {exposures !== null ? (
            <Badge tone="neutral">
              {exposures.toLocaleString()} {row.unit === "user" ? "people" : "projects"}
            </Badge>
          ) : null}
          {/* Held away from what the code declares, i.e. away from what a freshly
              deployed environment runs. A WARNING and nothing more: drifting is what
              an experiment is FOR, and most rows carrying this badge are doing their
              job. What it answers is the question nobody could ask before pressing
              Restore defaults: which of these ninety-six is this environment holding? */}
          {ships ? (
            <Badge tone="accent">the code ships {ships}</Badge>
          ) : null}
          {offDefault ? (
            <Badge tone="warning">
              not at default ({formatAllocation(row.defaultAllocation)})
            </Badge>
          ) : null}
        </div>
        <p className="exb-card__key">
          <code>{row.key}</code>
        </p>
        {row.description ? <p className="exb-card__blurb">{row.description}</p> : null}
        <p className="exb-card__shares">{summariseAllocation(row)}</p>
      </div>
      <div className="exb-card__control">
        {dialable ? (
          // `focused` only while the card is OPEN. The board mounts one of these
          // per experiment, so a control that always reads its own allocation is a
          // ninety-five-request page load, not a per-card cost - see
          // `liveAllocationKey`. A closed card reads the same snapshot row the
          // sentence above its slider already reads, and reaching for the slider
          // (which this board deliberately allows without opening the card) reads
          // live on its own before any move can be applied.
          <AllocationEditor record={row} canAct={canAct} compact focused={open} />
        ) : (
          <p className="exb-card__nodial">
            No allocation to move: this kind is recorded, not dialled.
          </p>
        )}
      </div>
    </div>
  );
}

export function ExperimentBoard({
  records,
  canAct,
  accessKnown,
  onShowTable,
}: {
  records: readonly AdminExperimentRecord[];
  canAct: boolean;
  accessKnown: boolean;
  onShowTable: () => void;
}) {
  const [kind, setKind] = useState<string>("");
  const [query, setQuery] = useState("");
  // A KEY, not an object, so the panel survives the snapshot refetching under it
  // after a dial moves.
  const [openKey, setOpenKey] = useState<string>("");
  const [openKind, setOpenKind] = useState<string>("");

  const kinds = useMemo(() => {
    const present = new Set(records.map((r) => r.kind));
    return KIND_GROUP_ORDER.filter((k) => present.has(k));
  }, [records]);

  const active = kind || kinds[0] || "";
  const mine = useMemo(() => records.filter((r) => r.kind === active), [records, active]);

  const needle = query.trim().toLowerCase();
  const rows = useMemo(
    () => (needle ? mine.filter((r) => registerHaystack(r).includes(needle)) : mine),
    [mine, needle],
  );

  const splits = rows.filter((r) => stateOf(r) === "split").length;
  const dialable = rows.filter((r) => allocatableKind(r.kind) !== null).length;

  return (
    <div className="exb">
      <div className="exb-tabs" role="tablist" aria-label="Kind">
        {kinds.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={name === active}
            className={name === active ? "exb-tab is-active" : "exb-tab"}
            onClick={() => {
              setKind(name);
              setOpenKey("");
            }}
          >
            {groupLabel(name)} ({records.filter((r) => r.kind === name).length})
          </button>
        ))}
        <Button size="sm" variant="ghost" tone="neutral" onClick={onShowTable}>
          Table view
        </Button>
      </div>

      <div className="exb-toolbar">
        <input
          className="rc-input exb-search"
          type="search"
          value={query}
          placeholder="Search this kind by key, name, description, arm or owner"
          aria-label="Search experiments"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="exb-summary">
        <p className="exb-summary__line">
          <strong>{groupLabel(active)}</strong>: {rows.length}{" "}
          {rows.length === 1 ? "experiment" : "experiments"}
          {needle ? ` matching "${query.trim()}"` : ""}, {dialable} with a dial you can move.
        </p>
        <p className="exb-summary__facts">
          {splits > 0
            ? `${splits} splitting traffic right now`
            : "None of these is splitting traffic"}
          {accessKnown && !canAct ? " · your access is read only, so the controls are disabled" : ""}
        </p>
      </div>

      <div className={openKey ? "exb-body is-split" : "exb-body"}>
        <div className="exb-list">
          {rows.map((row) => (
            <Card
              key={`${row.kind}:${row.key}`}
              row={row}
              canAct={canAct}
              open={openKey === row.key && openKind === row.kind}
              onOpen={() => {
                const same = openKey === row.key && openKind === row.kind;
                setOpenKey(same ? "" : row.key);
                setOpenKind(same ? "" : row.kind);
              }}
            />
          ))}
          {rows.length === 0 ? (
            <p className="exb-empty">
              {needle
                ? `Nothing in ${groupLabel(active)} matches "${query.trim()}".`
                : `No ${groupLabel(active)} experiments are declared.`}
            </p>
          ) : null}
        </div>
        {openKey ? (
          <ExperimentInspector
            experimentKey={openKey}
            kind={openKind}
            onClose={() => {
              setOpenKey("");
              setOpenKind("");
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
